
    // IMPORTS
    import { computed, defineComponent } from 'vue'
    import router from '@/router'
    import { useUserStateStore } from '@/store/userState'
    import { useAdminEquipmentsStore } from '@/store/adminEquipments'

    import MyGrid from '@/components/grid/my-grid.vue'
    import Lazy from '@/components/common/lazy-render.vue'

    import { SortDescriptor } from '@progress/kendo-data-query'
    import { GridColumnProps } from '@progress/kendo-vue-grid'
    import { buildLocalization } from '@/util'
    import { AdminEquipmentRow, IEquipmentMasterDataWithBomWarningCount } from '@/types/equipment'
    import Loader from '@/components/common/loader.vue'
    import EquipmentSerialNumberCell from '@/components/grid/cells/equipment-serial-number-cell.vue'
    import DateFilterCell from '@/components/grid/cells/date-filter-cell.vue'

    import iconBlocked from '@/img/icon-blocked.png'
    import iconReleased from '@/img/icon-released.png'
    import iconInProgress from '@/img/icon-in-progress.png'
    import iconCompleted from '@/img/icon-completed.png'
    import iconDeliveredToStock from '@/img/icon-delivered-to-stock.png'
    import iconWarning from '@/img/icon-warning.png'

    export default defineComponent({
        name: 'adminEquipmentList',

        components: {
            Lazy,
            MyGrid,
            Loader,
            EquipmentSerialNumberCell
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const userStateStore = useUserStateStore()
            const adminEquipmentsStore = useAdminEquipmentsStore()

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const publicData = { }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const allEquipments = computed(() => adminEquipmentsStore.devices)
            const equipmentRows = computed(() => allEquipments.value != null
                ? transform(allEquipments.value)
                : null
            )

            const iconWarningPath = computed(() => iconWarning)

            const initialSort = computed<SortDescriptor[]>(() => [{ field: 'deliveryDate', dir: 'desc' }])

            const gridState = computed(() => userStateStore.adminDevicesState.value)
            
            const columns = computed(() => [
                    {
                        field: 'serialNo',
                        title: localize('serialNo'),
                        width: 150,
                        cell: 'custom1'
                    },
                    {
                        field: 'materialNo',
                        title: localize('materialNo'),
                        width: 150
                    },
                    {
                        field: 'description',
                        title: localize('description'),
                    },
                    {
                        field: 'soldToInfo',
                        title: localize('soldTo')
                    },
                    {
                        field: 'countryCode',
                        title: localize('partnerCountry'),
                        width: 130
                    },
                    //{
                    //    field: 'salesOrderRef',
                    //    title: localize('salesOrderRef'),
                    //},
                    //{
                    //    field: 'contactPersonInfo',
                    //    title: localize('contactPerson'),
                    //},
                    {
                        field: 'productionOrderNo',
                        title: localize('productionOrderNo'),
                        width: 150
                    },
                    {
                        field: 'salesOrderNo',
                        title: localize('salesOrderNo'),
                        width: 150
                    },
                    {
                        field: 'deliveryDate',
                        title: localize('shippingDate'),
                        cell: 'date',
                        filter: 'date',
                        filterCell: DateFilterCell,
                        width: 200
                    },
                    {
                        field: 'status',
                        title: localize('status'),
                        cell: 'custom2'
                    },
                    {
                        field: 'equipmentBomHasBeenCrated',
                        title: localize('bomCreated'),
                        filter: 'boolean',
                        cell: 'bool',
                        width: 150
                    },
                    {
                        field: 'bomWarningCount',
                        title: localize('warningCount'),
                        type: 'numeric',
                        filter: 'numeric',
                        cell: 'custom3',
                        width: 150
                    }                        
                ] as GridColumnProps[]                
            )

            const computedData = { allEquipments, equipmentRows, iconWarningPath, initialSort, gridState, columns }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function getId(equipmentNo: number): string {
                return `EQ${equipmentNo}`
            }

            function redirectToDevice(device: AdminEquipmentRow) {
                gridState.value.idOfCurrentRow = getId(device.equipmentNo)
                router.push(`/equipment/${device.serialNo}`)
            }

            function showWarnings(equipmentNo: number) {
                gridState.value.idOfCurrentRow = getId(equipmentNo)
                router.push(`/warnings/${equipmentNo}`)
            }

            function statusColor(device: AdminEquipmentRow): ({ color: string }) {
                if (device.productionOrderCompleted) {
                    return { color: 'black' }
                }

                if (device.productionOrderReleased) {
                    return { color: 'black' }
                }

                if (device.productionOrderReleasedBySales) {
                    return { color: 'limeGreen' }
                }

                return { color: 'red' }
            }

            function statusIcon(device: AdminEquipmentRow): string {
                if (device.productionOrderCompleted) {
                    return iconCompleted
                }

                if (device.productionOrderDeliveredToStock) {
                    return iconDeliveredToStock
                }

                if (device.productionOrderReleased) {
                    return iconInProgress
                }

                if (device.productionOrderReleasedBySales) {
                    return iconReleased
                }

                return iconBlocked
            }

            const publicMethods = { localize, getId, redirectToDevice, showWarnings, statusColor, statusIcon }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function transform(equipments: IEquipmentMasterDataWithBomWarningCount[]): AdminEquipmentRow[] {
                const result = equipments.map(device => new AdminEquipmentRow(localize, device))
                return result
            }

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
